import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TipoContribuzione } from 'app/main/forfettario-folder/dichiarazione/dischiarazione.models';
import { CodiceCCIAA } from 'app/main/forfettario-folder/forfettario.models';
import { BehaviorSubject, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ForfettappApiClient, ForfettappApiClientOptions } from './forfettapp-api-client';
import { LoaderHandlingMode } from './models';
@Injectable({
    providedIn: 'root',
})
export class CommonService {
    constructor(private service: ForfettappApiClient) {}
    private _nullPecMessage = `Non puoi validare questo documento poiché il forfettario non ha inserito l'indirizzo PEC !`;
    private _notValidAddressMessage = `Prima di confermare il documento è necessario inserire i dati di residenza`;
    private _contribuzioni = new BehaviorSubject<Contribuzione[]>(null);
    private _ateco = new BehaviorSubject<Ateco[]>(null);
    private _province = new BehaviorSubject<Provincia[]>(null);

    get nullPecMessage() {
        return this._nullPecMessage;
    }
    get notValidAddressMessage() {
        return this._notValidAddressMessage;
    }

    get contribuzioni$() {
        return this._contribuzioni.pipe(
            switchMap(contribuzioni => {
                if (contribuzioni === null) {
                    return this.getContribuzioniList();
                } else {
                    return of(contribuzioni);
                }
            }),
        );
    }
    get province$() {
        return this._province.pipe(
            switchMap(province => {
                if (province === null) {
                    return this.getProvince();
                } else {
                    return of(province);
                }
            }),
        );
    }
    get ateco$() {
        return this._ateco.pipe(
            switchMap(ateco => {
                if (ateco === null) {
                    return this.getAtecoList();
                } else {
                    return of(ateco);
                }
            }),
        );
    }

    getAtecoFromCacheByCodice(codiceAteco: string) {
        if (!this._ateco) return null;

        return this._ateco.getValue().find(x => x.id == codiceAteco);
    }

    getAtecoList(decorrenza?: number, cacheResult: boolean = true) {
        let params = new HttpParams();
        if (decorrenza) params = params.set('decorrenza', decorrenza.toString());

        return this.service.get<Ateco[]>(`/ateco`, params).pipe(
            tap(ateco => {
                if (cacheResult) this._ateco.next(ateco);
            }),
        );
    }

    updateAteco(atecoToUpdate: Ateco) {
        return this.service.post<Ateco[]>(`/backoffice/tabelle/ateco/${atecoToUpdate.id}`, atecoToUpdate);
    }

    getElencoCCIAA() {
        return this.service.get<CodiceCCIAA[]>(`/backoffice/elenco-cciaa`);
    }

    getContribuzioniList() {
        return this.service.get<Contribuzione[]>(`/backoffice/elenco-contribuzioni`).pipe(
            tap(contribuzioni => {
                this._contribuzioni.next(contribuzioni);
            }),
        );
    }

    getProvince() {
        return this.service.get<Provincia[]>(`/geo/province`).pipe(
            tap(province => {
                this._province.next(province);
            }),
        );
    }

    getRegioniProvinceAutonome() {
        return this.service.get<RegioniProvinceAutonome[]>('/geo/regioni-autonome');
    }

    getComuni(provincia: string, clientOptions?: ForfettappApiClientOptions) {
        var header = new HttpHeaders();
        if (clientOptions?.cacheData) header = header.set('cache-data', String(clientOptions.cacheData));
        if (clientOptions?.refreshData) header = header.set('refresh-data', String(clientOptions.refreshData));
        return this.service.get<Comune[]>({ url: `/geo/province/${provincia}/comuni`, headers: header });
    }

    getComuniByCodCatastaleOrDenominazione(ricerca: string) {
        return this.service.get<Comune[]>('/geo/comuni', new HttpParams().set('ricerca', ricerca), null, LoaderHandlingMode.useLocalLoaderHandler);
    }
}

export interface RegioniProvinceAutonome {
    id: string;
    descrizione: string;
}

export interface Ateco {
    id: string;
    descrizione: string;
    enasarco: boolean;
    lavoroSportivo: boolean;
    lavoroEticamenteSensibile: boolean;
    decorrenza: number;
}

export interface Provincia {
    codice: string;
    descrizione: string;
}

export interface Comune {
    cap: string[];
    codiceCatastale: string;
    codiceIstat: string;
    codiceRegioneProvinciaAutonoma: string;
    nominativo: string;
    provincia: string;
}
export interface Contribuzione {
    id: TipoContribuzione;
    descrizione: string;
    descrizioneInFattura: string;
}

export interface ModelloReport {
    descrizione: string;
    id: number;
    nome: string;
    tipologia: string;
    urlAnteprima: string;
}
